import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { Box, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

// Components
import NavBar from '../../components/NavBarSearchSong';
import MultipleSelectCheckmarks from '../../components/MultiSelector';
import { SvgList } from '../../components';
import Button from '../../components/Button';
import SelectedSongsListSection from './SelectedSongsListSection';
import { _showAlert } from '../../utils';
import ProgressBar from '../../components/ProgressBar';

// Images
import Settings from '../../assets/Settings.png';

// Styles
import '../AddSong/Addsong.scss';

import { songActions } from '../../actions';

const useStyles = makeStyles({
  table: {
    minWidth: 700,
    borderRadius: 30,
  },
  cancelButton: {
    borderRadius: 46,
    background: '#E7DDFF',
    boxShadow: 'none',
    padding: '12px 28px',
    border: '1px solid transparent',
    minWidth: 150,
    cursor: 'pointer',

    '&:hover': {
      boxShadow: 'none',
    },
  },
});

function AddSongs() {
  const history = useHistory();
  const [types, setTypes] = useState([]);
  const [newSelectedSongsList, setNewSelectedSongs] = useState([]);
  const [isUploadingSongs, setUploadingSongs] = useState(false);
  const classes = useStyles();

  /**
   * Add Songs functionality
   */
  const handleUpload = () => {
    document.getElementById('song-picker').click();
  };

  const handleSongUpload = () => {
    const getAudioDuration = (file) => {
      return new Promise((resolve) => {
        const audio = new Audio();
        audio.src = URL.createObjectURL(file);
        audio.onloadedmetadata = () => {
          URL.revokeObjectURL(audio.src);
          resolve(Math.round(audio.duration));
        };
        audio.onerror = () => {
          resolve(0);
        };
      });
    };

    const postSongs = async () => {
      try {
        let successCount = 0;

        for (let i = 0; i < newSelectedSongsList.length; i++) {
          const file = newSelectedSongsList[i];

          // Step 1: Get Signed Upload URL
          const signedUrlResponse = await songActions.getSongUploadUrl({
            audio: {
              filename: file.name,
              mimetype: file.type || 'audio/mpeg',
            },
          });

          if (!signedUrlResponse || signedUrlResponse.code !== 'OK') {
            throw new Error(`Failed to get upload authorization for "${file.name}".`);
          }

          const { song_id, signed_url, key } = signedUrlResponse.data;

          // Step 2: Upload File to R2
          const uploadResponse = await fetch(signed_url, {
            method: 'PUT',
            headers: {
              'Content-Type': file.type || 'audio/mpeg',
            },
            body: file,
          });

          if (!uploadResponse.ok) {
            throw new Error(`Failed to upload file "${file.name}" to storage.`);
          }

          // Get Duration
          const duration = await getAudioDuration(file);

          // Step 3: Save Song Record
          const savePayload = {
            song_id,
            audio_key: key,
            title: file.name.replace(/\.[^/.]+$/, ''), // Remove extension for title
            duration: duration,
            type: types.join(','),
            artist_name: '', // Optional as per table
            genre: '', // Optional as per table
            description: '', // Optional as per table
            thumbnail_img: '', // Optional as per table
          };

          const saveResult = await songActions.postSong(savePayload);

          if (!saveResult) {
            throw new Error(`Failed to save song record for "${file.name}".`);
          }
          successCount++;
        }

        if (newSelectedSongsList.length > 0) {
          _showAlert(`${successCount}/${newSelectedSongsList.length} Songs uploaded successfully.`, 4000);
          setNewSelectedSongs([]);
          setTypes([]);
          setUploadingSongs(false);
          history.push('/songs/search-songs');
        }

        if (!newSelectedSongsList) {
          console.error('No audio file selected.');
          return;
        }
      } catch (error) {
        console.error('Error uploading file:', error);
        _showAlert(error.message || 'Error uploading file. Please try again.', 'error', 4000);
        setUploadingSongs(false);
      }
    };

    if (newSelectedSongsList.length > 0 && types.length > 0) {
      setUploadingSongs(true);
      postSongs();
    } else {
      _showAlert('Please select song plan.', 'error', 4000);
    }
  };

  const renderView = () => {
    if (isUploadingSongs) {
      return (
        <Box width="100%" height="60vh" display="flex" justifyContent="center" alignItems="center">
          <Box width="40%" textAlign="center">
            <Typography className="uploading-text">Uploading...</Typography>
            <ProgressBar />
          </Box>
        </Box>
      );
    }

    return (
      <>
        <p className="title">Choose Plan</p>

        <div className="firstrowinfo">
          <MultipleSelectCheckmarks onChange={(selectedType) => setTypes(selectedType)} />
          {newSelectedSongsList?.length > 0 && (
            <div className="btn_row">
              <Button className={classes.cancelButton} disabled={isUploadingSongs} onClick={() => setNewSelectedSongs([])}>
                Cancel
              </Button>
              <Button onClick={handleSongUpload} isLoading={isUploadingSongs} className="round_btn">
                Upload
              </Button>
            </div>
          )}
        </div>
        {newSelectedSongsList?.length > 0 ? (
          <SelectedSongsListSection songs={newSelectedSongsList} />
        ) : (
          <>
            <div className="section">
              <div className="uploadsongWrapper">
                <Typography className="customTypography" style={{ cursor: 'auto' }}>
                  Upload Songs
                </Typography>
                <div className="border_Info">
                  <div className="addSong">
                    <SvgList name="add-songs" />
                  </div>
                  <p className="info">Browse and chose the files you want to upload from your computer</p>

                  <div className="buttonbox" onClick={handleUpload}>
                    <SvgList name="plus-icon" />
                  </div>
                </div>
              </div>
            </div>
            <input
              type="file"
              id="song-picker"
              style={{ display: 'none' }}
              onChange={(e) => setNewSelectedSongs(e.target.files)}
              multiple={true}
              accept="audio/mp3,audio/*;capture=microphone"
            />
          </>
        )}
      </>
    );
  };

  return (
    <>
      <NavBar
        data="Add song"
        icon={Settings}
        currentRouteName="Add song"
        breadcrumbsPath={[{ name: 'Songs', link: '/songs/search-songs' }]}
      />
      <div className="content empty_row">{renderView()}</div>
    </>
  );
}
export default AddSongs;

